import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as Sentry from '@sentry/gatsby'
import { getApp, getApps, initializeApp } from 'firebase/app'
import { Auth, getAuth, onAuthStateChanged, signOut, User } from 'firebase/auth'

const firebaseConfig = {
  apiKey: process.env.GATSBY_FIREBASE_API_KEY,
  authDomain: process.env.GATSBY_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.GATSBY_FIREBASE_PROJECT_ID,
  storageBucket: process.env.GATSBY_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.GATSBY_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.GATSBY_FIREBASE_APP_ID,
}

type FirebaseAuthProviderProps = {
  authorizedRoles: AuthRole[]
  children: React.ReactNode
}

export enum AuthRole {
  ADMIN = 'admin',
  INVESTOR = 'investor',
  CLIENT = 'client',
  SKIP_MFA = 'skip_mfa',
}

export enum FirebaseAuthStatus {
  LOADING = 'loading',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
  ERROR = 'error',
}

type FirebaseAuthContextProps = {
  user: User | null
  auth: Auth
  authStatus: FirebaseAuthStatus
  authorizedRoles: AuthRole[]
  userRoles: AuthRole[]
  logout: () => Promise<void>
  retryAuth: () => Promise<void>
  useValidateAuthLoadedAndLogout: () => void
}

export const FirebaseAuthContext = createContext<FirebaseAuthContextProps>({
  user: null,
  auth: {} as Auth,
  authStatus: FirebaseAuthStatus.LOADING,
  authorizedRoles: [],
  userRoles: [],
  logout: async () => {},
  retryAuth: async () => {},
  useValidateAuthLoadedAndLogout: () => {},
})

type AuthFailureStage =
  | 'initialization'
  | 'token-validation'
  | 'authorization-sign-out'

type FirebaseAuthState = {
  authStatus: FirebaseAuthStatus
  user: User | null
  userRoles: AuthRole[]
}

const getEmptyAuthState = (
  authStatus: FirebaseAuthStatus
): FirebaseAuthState => ({
  authStatus,
  user: null,
  userRoles: [],
})

const AUTH_OPERATION_TIMEOUT_MS = 15000

const normalizeError = (error: unknown, fallbackMessage: string): Error =>
  error instanceof Error ? error : new Error(fallbackMessage)

export const reportFirebaseAuthFailure = (
  error: unknown,
  stage: string,
  code?: string
) => {
  const normalizedError = normalizeError(
    error,
    'An unexpected Firebase authentication operation failed.'
  )
  Sentry.captureException(normalizedError, {
    tags: {
      firebase_auth_code: code || 'unknown',
      firebase_auth_stage: stage,
    },
  })
}

const withTimeout = async <T,>(
  promise: Promise<T>,
  message: string
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error(message)),
      AUTH_OPERATION_TIMEOUT_MS
    )
  })

  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

const getUserRoles = (claims: Record<string, unknown>): AuthRole[] => {
  const roles: AuthRole[] = []
  Object.entries(claims).forEach(([key, value]) => {
    if (Object.values(AuthRole).includes(key as AuthRole) && value === true) {
      roles.push(key as AuthRole)
    }
  })
  return roles
}

// Non-reactive access to the signed-in user for callers that must not
// re-render on auth state changes (e.g. Apollo link token fetching).
export const getCurrentFirebaseUser = (): User | null =>
  getApps().length ? getAuth().currentUser : null

const FirebaseAuthProvider = (props: FirebaseAuthProviderProps) => {
  const { authorizedRoles, children } = props

  // Call sites pass a freshly created `authorizedRoles` array each render, so
  // depend on the role *values* (a stable key) rather than the array identity.
  // Otherwise effects/memos below rerun every parent render and re-register
  // `onAuthStateChanged` listeners / re-init Firebase.
  const authorizedRolesKey = authorizedRoles.join(',')
  const stableAuthorizedRoles = useMemo(
    () => authorizedRoles,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authorizedRolesKey]
  )

  // Keep the user, roles, and status in one state object. React 17 does not
  // batch every async update, so separate setters could briefly render an
  // authenticated status with a cleared user and show the recovery screen.
  const [authState, setAuthState] = useState<FirebaseAuthState>(() =>
    getEmptyAuthState(FirebaseAuthStatus.LOADING)
  )
  const [authObj, setAuthObj] = useState<Auth | null>(null)
  const [initializationVersion, setInitializationVersion] = useState(0)
  // A retry or newer auth event supersedes any older token request. This keeps
  // a late failure from replacing a session that has already validated.
  const authAttemptRef = useRef(0)

  const validateUser = useCallback(
    async (auth: Auth, user: User | null, forceRefresh = false) => {
      const attempt = authAttemptRef.current + 1
      authAttemptRef.current = attempt

      if (!user) {
        setAuthState(getEmptyAuthState(FirebaseAuthStatus.UNAUTHENTICATED))
        return
      }

      setAuthState(getEmptyAuthState(FirebaseAuthStatus.LOADING))
      let failureStage: AuthFailureStage = 'token-validation'

      try {
        const idTokenResult = await withTimeout(
          user.getIdTokenResult(forceRefresh),
          'Firebase timed out while validating this login.'
        )
        if (attempt !== authAttemptRef.current) {
          return
        }

        const tokenUserRoles = getUserRoles(idTokenResult.claims)
        if (
          !tokenUserRoles.some((role) =>
            stableAuthorizedRoles.includes(role as AuthRole)
          )
        ) {
          failureStage = 'authorization-sign-out'
          await withTimeout(
            signOut(auth),
            'Firebase timed out while signing out an unauthorized login.'
          )
          if (attempt !== authAttemptRef.current) {
            return
          }
          setAuthState(getEmptyAuthState(FirebaseAuthStatus.UNAUTHENTICATED))
          return
        }

        setAuthState({
          authStatus: FirebaseAuthStatus.AUTHENTICATED,
          user,
          userRoles: tokenUserRoles,
        })
      } catch (rawError) {
        if (attempt !== authAttemptRef.current) {
          return
        }
        const error = normalizeError(
          rawError,
          'Firebase failed while validating this login.'
        )
        reportFirebaseAuthFailure(error, failureStage)
        setAuthState(getEmptyAuthState(FirebaseAuthStatus.ERROR))
      }
    },
    [stableAuthorizedRoles]
  )

  const useValidateAuthLoadedAndLogout = useCallback(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (!authObj) {
        return undefined
      }

      return onAuthStateChanged(authObj as Auth, async () => {
        await signOut(authObj as Auth)
        window.location.href = '/login'
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authObj])
  }, [authObj])

  useEffect(() => {
    const initializationAttempt = authAttemptRef.current + 1
    authAttemptRef.current = initializationAttempt
    let initializationTimeout: ReturnType<typeof setTimeout> | undefined
    let unsubscribe: (() => void) | undefined

    try {
      // Reuse the already-initialized app if present (StrictMode double-invoke,
      // re-run, etc.) instead of calling initializeApp twice.
      const app = getApps().length ? getApp() : initializeApp(firebaseConfig)
      const auth = getAuth(app)
      setAuthObj(auth)
      setAuthState(getEmptyAuthState(FirebaseAuthStatus.LOADING))

      // Replace indefinite loading with recovery UI, but leave the observer
      // active so a late Firebase response can still authenticate the user.
      initializationTimeout = setTimeout(() => {
        if (initializationAttempt !== authAttemptRef.current) {
          return
        }
        const error = new Error(
          'Firebase timed out while restoring the saved login.'
        )
        reportFirebaseAuthFailure(error, 'initialization')
        setAuthState(getEmptyAuthState(FirebaseAuthStatus.ERROR))
      }, AUTH_OPERATION_TIMEOUT_MS)

      unsubscribe = onAuthStateChanged(
        auth,
        (user) => {
          if (initializationTimeout) {
            clearTimeout(initializationTimeout)
          }
          validateUser(auth, user)
        },
        (rawError) => {
          if (initializationTimeout) {
            clearTimeout(initializationTimeout)
          }
          // This observer belongs to the initialization attempt created by this
          // effect. A retry can complete while Firebase is still delivering the
          // older observer error, so do not replace the newer result.
          if (initializationAttempt !== authAttemptRef.current) {
            return
          }
          const error = normalizeError(
            rawError,
            'Firebase failed while restoring the saved login.'
          )
          reportFirebaseAuthFailure(error, 'initialization')
          setAuthState(getEmptyAuthState(FirebaseAuthStatus.ERROR))
        }
      )
    } catch (rawError) {
      if (initializationTimeout) {
        clearTimeout(initializationTimeout)
      }
      if (initializationAttempt === authAttemptRef.current) {
        const error = normalizeError(
          rawError,
          'Firebase failed while initializing the login service.'
        )
        reportFirebaseAuthFailure(error, 'initialization')
        setAuthObj(null)
        setAuthState(getEmptyAuthState(FirebaseAuthStatus.ERROR))
      }
    }

    return () => {
      if (initializationTimeout) {
        clearTimeout(initializationTimeout)
      }
      authAttemptRef.current += 1
      unsubscribe?.()
    }
  }, [initializationVersion, validateUser])

  const retryAuth = useCallback(async () => {
    if (!authObj) {
      // A synchronous SDK/configuration failure has no Auth object to retry.
      // Remount the initialization effect instead of returning to the same
      // unrecoverable error screen.
      setAuthState(getEmptyAuthState(FirebaseAuthStatus.LOADING))
      setInitializationVersion((version) => version + 1)
      return
    }

    const attempt = authAttemptRef.current + 1
    authAttemptRef.current = attempt
    setAuthState(getEmptyAuthState(FirebaseAuthStatus.LOADING))

    try {
      // `authStateReady` distinguishes a settled signed-out session from a
      // Firebase client that is still restoring browser persistence.
      await withTimeout(
        authObj.authStateReady(),
        'Firebase timed out while retrying the saved login.'
      )
      if (attempt !== authAttemptRef.current) {
        return
      }
      await validateUser(authObj, authObj.currentUser, true)
    } catch (rawError) {
      if (attempt !== authAttemptRef.current) {
        return
      }
      const error = normalizeError(
        rawError,
        'Firebase failed while retrying the saved login.'
      )
      reportFirebaseAuthFailure(error, 'initialization')
      setAuthState(getEmptyAuthState(FirebaseAuthStatus.ERROR))
    }
  }, [authObj, validateUser])

  const logout = useCallback(async () => {
    if (authObj) {
      await signOut(authObj)
    }
  }, [authObj])

  const contextValue = useMemo(
    () => ({
      user: authState.user,
      userRoles: authState.userRoles,
      auth: authObj as Auth,
      authStatus: authState.authStatus,
      authorizedRoles: stableAuthorizedRoles,
      logout,
      retryAuth,
      useValidateAuthLoadedAndLogout,
    }),
    [
      authState,
      authObj,
      stableAuthorizedRoles,
      logout,
      retryAuth,
      useValidateAuthLoadedAndLogout,
    ]
  )

  return (
    <FirebaseAuthContext.Provider value={contextValue}>
      {children}
    </FirebaseAuthContext.Provider>
  )
}

export const useFirebaseAuth = () => {
  const context = useContext(FirebaseAuthContext)
  if (context === undefined) {
    throw new Error(
      'useFirebaseAuth must be used within a FirebaseAuthProvider'
    )
  }
  return context
}

export default FirebaseAuthProvider
